import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Lesson, Booking, Student, hasAnyRole } from '@court-sync/shared';
import { lessonService } from '../../../../services/lesson';
import { bookingService } from '../../../../services/booking';
import { studentService } from '../../../../services/student';
import { LessonPaymentForm } from '../../../../components/LessonPaymentForm';
import { Card, CardContent, CardHeader } from '../../../../components/ui/Card';
import { Button, PhoneInput, PhoneNumberLabel } from '@/components/ui';
import { Alert, AlertDescription } from '../../../../components/ui/Alert';
import { Badge } from '../../../../components/ui/Badge';
import {
  Calendar,
  Clock,
  User,
  MapPin,
  DollarSign,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Users,
  Copy,
  Plus
} from 'lucide-react';
import { useAuthStore } from '../../../../store/auth';
import { useGuardianPaths } from '../../../../hooks/useGuardianPaths';
import { toast } from 'react-hot-toast';
import { termService } from '../../../../services/term';
import { isLessonBookable, getLessonBookingReason } from '../../../../utils/lessonBooking';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useAcademySports } from '@/hooks/useAcademySports';
import { SportBadge } from '@/components/sport/SportBadge';
import { resolveAcademySport } from '@/lib/sportPresentation';

export default function BookLesson() {
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);
  const { sports: academySports, isMultiSport } = useAcademySports(guardianAcademy);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [term, setTerm] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [duplicateBookingError, setDuplicateBookingError] = useState(false);
  const [studentFormSubmitting, setStudentFormSubmitting] = useState(false);
  const [studentFormData, setStudentFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'male' as 'male' | 'female' | 'other',
    skillLevel: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    medicalNotes: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: '',
    },
  });

  // Get booking ID from URL parameters if provided
  const existingBookingId = searchParams.get('bookingId');
  const isResumeMode = Boolean(existingBookingId);

  useEffect(() => {
    if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
      navigate('/login');
      return;
    }

    if (existingBookingId) {
      void loadExistingBooking(existingBookingId);
      return;
    }

    loadStudents();

    if (lessonId) {
      loadLessonDetails();
    }
  }, [user, navigate, lessonId, existingBookingId]);

  // Prefill form with the most recently used student data
  const prefillWithLastStudent = () => {
    if (students.length > 0) {
      const lastStudent = students[0]; // Assuming students are ordered by most recent
      setStudentFormData({
        firstName: lastStudent.firstName,
        lastName: lastStudent.lastName,
        dateOfBirth: new Date(lastStudent.dateOfBirth).toISOString().split('T')[0],
        gender: lastStudent.gender,
        skillLevel: lastStudent.skillLevel || 'beginner',
        medicalNotes: lastStudent.medicalNotes || '',
        emergencyContact: {
          name: lastStudent.emergencyContact?.name || '',
          phone: lastStudent.emergencyContact?.phone || '',
          relationship: lastStudent.emergencyContact?.relationship || '',
        },
      });
      toast.success('Form prefilled with last used student details');
    }
  };

  // Helper function to handle API errors with specific messages
  const handleApiError = (error: any, defaultMessage: string) => {
    console.error('API Error:', error);

    // Check for specific error messages in different possible locations
    const errorMessage = error.response?.data?.error ||
      error.response?.data?.message ||
      error.message;

    if (errorMessage === 'Customer already has a booking for this lesson' ||
      errorMessage?.includes('already has a booking') ||
      errorMessage?.includes('duplicate booking')) {
      setDuplicateBookingError(true);
      toast.error('You already have a booking for this lesson. Please check your bookings or choose a different lesson.');
    } else {
      setDuplicateBookingError(false);
      toast.error(errorMessage || defaultMessage);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await studentService.getStudents();
      setStudents(response.data);
    } catch (error: any) {
      console.error('Error loading students:', error);
      // Don't set error state here as it's not critical for the main flow
    }
  };

  const loadLessonDetails = async () => {
    try {
      setLoading(true);
      const response = await lessonService.getLesson(lessonId!);
      const lessonData = response.data;
      setLesson(lessonData);

      // Load term if this is a group/clinic lesson
      if (lessonData.termId && lessonData.lessonType !== 'private') {
        try {
          const termResponse = await termService.getTerm(lessonData.termId);
          setTerm(termResponse.data);
        } catch (termError) {
          console.error('Error loading term:', termError);
          // Don't fail the whole page if term loading fails
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load lesson details');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingBooking = async (bookingId: string) => {
    try {
      setLoading(true);
      const response = await bookingService.getBooking(bookingId);
      const existingBooking = response.data;

      if (lessonId && lessonId !== existingBooking.lessonId && paths?.bookLessonPay) {
        navigate(paths.bookLessonPay(existingBooking.lessonId, bookingId), { replace: true });
        return;
      }

      const lessonResponse = await lessonService.getLesson(existingBooking.lessonId);
      const lessonData = lessonResponse.data;
      setLesson(lessonData);
      setBooking(existingBooking);

      if (lessonData.termId && lessonData.lessonType !== 'private') {
        try {
          const termResponse = await termService.getTerm(lessonData.termId);
          setTerm(termResponse.data);
        } catch (termError) {
          console.error('Error loading term:', termError);
        }
      }

      if (existingBooking.studentId) {
        try {
          const studentResponse = await studentService.getStudent(existingBooking.studentId);
          setSelectedStudent(studentResponse.data);
        } catch (studentError) {
          console.error('Error loading student for booking:', studentError);
        }
      }

      if (existingBooking.paymentStatus === 'pending') {
        setShowPayment(true);
      } else if (existingBooking.paymentStatus === 'paid') {
        setBookingSuccess(true);
      }
    } catch (error: any) {
      console.error('Error loading existing booking:', error);
      if (error.response?.status === 403) {
        setError(
          'This payment link is for a different account. Sign in with the guardian account that requested this booking.'
        );
        toast.error('You do not have access to this booking');
      } else {
        handleApiError(error, 'Failed to load booking details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookLesson = async () => {
    if (!lesson || !user) return;

    // Clear any previous duplicate booking error
    setDuplicateBookingError(false);

    // If we already have a booking, just show payment form
    if (booking) {
      setShowPayment(true);
      return;
    }

    // If no student selected and form is not shown, show student form
    if (!selectedStudent && !showStudentForm) {
      setShowStudentForm(true);
      return;
    }

    // If form is shown but not filled, validate and create student
    if (showStudentForm) {
      if (!studentFormData.firstName || !studentFormData.lastName || !studentFormData.dateOfBirth) {
        toast.error('Please fill in all required student details');
        return;
      }

      try {
        setBookingLoading(true);
        setStudentFormSubmitting(true);

        // Create student first
        const studentData = {
          firstName: studentFormData.firstName,
          lastName: studentFormData.lastName,
          dateOfBirth: new Date(studentFormData.dateOfBirth),
          gender: studentFormData.gender,
          skillLevel: studentFormData.skillLevel,
          medicalNotes: studentFormData.medicalNotes || undefined,
          emergencyContact: studentFormData.emergencyContact.name ? studentFormData.emergencyContact : undefined,
        };

        const studentResponse = await studentService.createStudent(studentData);
        const newStudent = {
          id: studentResponse.data.id,
          parentId: user.id,
          ...studentData,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Student;

        setSelectedStudent(newStudent);
        setShowStudentForm(false);

        // Now create booking
        const bookingData = {
          lessonId: lesson.id,
          termId: lesson.termId,
          academyId: lesson.academyId,
          studentId: newStudent.id,
        };

        const bookingResponse = await bookingService.createBooking(bookingData);
        const totalAmount = bookingResponse.data.totalAmount ?? lesson.price;
        const newBooking = {
          id: bookingResponse.data.id,
          ...bookingData,
          customerId: user.id,
          status: 'pending' as const,
          paymentStatus: 'pending' as const,
          totalAmount,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Booking;

        setBooking(newBooking);
        setShowPayment(true);

        // Refresh students list
        loadStudents();

      } catch (error: any) {
        handleApiError(error, 'Failed to create student and book lesson');
      } finally {
        setBookingLoading(false);
        setStudentFormSubmitting(false);
      }
      return;
    }

    // If we have a selected student, create booking directly
    if (selectedStudent) {
      try {
        setBookingLoading(true);

        const bookingData = {
          lessonId: lesson.id,
          termId: lesson.termId,
          academyId: lesson.academyId,
          studentId: selectedStudent.id,
        };

        const bookingResponse = await bookingService.createBooking(bookingData);
        const totalAmount = bookingResponse.data.totalAmount ?? lesson.price;
        const newBooking = {
          id: bookingResponse.data.id,
          ...bookingData,
          customerId: user.id,
          status: 'pending' as const,
          paymentStatus: 'pending' as const,
          totalAmount,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Booking;

        setBooking(newBooking);
        setShowPayment(true);
      } catch (error: any) {
        handleApiError(error, 'Failed to book lesson');
      } finally {
        setBookingLoading(false);
      }
    }
  };

  const handlePaymentSuccess = async () => {
    setBookingSuccess(true);
    toast.success('Payment successful! Your lesson is confirmed.');

    // Update booking status to confirmed
    if (booking) {
      try {
        await bookingService.updateBooking(booking.id, {
          status: 'confirmed',
          paymentStatus: 'paid',
        });
      } catch (error) {
        console.error('Error updating booking status:', error);
      }
    }
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
    setShowPayment(false);
  };

  const handlePaymentCancel = () => {
    if (isResumeMode) {
      navigate(paths?.bookings ?? '/academy-picker');
      return;
    }
    setShowPayment(false);
    setBooking(null);
  };

  const calculateAge = (dateOfBirth: Date) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen py-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Alert variant="destructive">
            <AlertDescription>Lesson not found</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const isFullyBooked = (lesson.currentEnrollment || 0) >= lesson.maxCapacity;
  const spotsRemaining = lesson.maxCapacity - (lesson.currentEnrollment || 0);
  const isBookable = isLessonBookable(lesson, term);
  const bookingReason = getLessonBookingReason(lesson, term);

  if (bookingSuccess) {
    return (
      <div className="min-h-screen py-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Booking Confirmed!
              </h3>
              <p className="text-gray-600 mb-4">
                Your lesson has been booked and payment processed successfully.
              </p>
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-green-800">
                  <strong>Lesson:</strong> {lesson.name}<br />
                  <strong>Date:</strong> {new Date().toLocaleDateString()}<br />
                  <strong>Amount:</strong> ${booking?.totalAmount ?? lesson.price}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(paths?.bookings ?? '/academy-picker')}
                  className="flex-1"
                >
                  View My Bookings
                </Button>
                <Button
                  onClick={() => navigate(paths?.lessons ?? '/academy-picker')}
                  className="flex-1"
                >
                  Book Another Lesson
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showPayment && booking) {
    return (
      <div className="min-h-screen py-2">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={handlePaymentCancel}
            className="-ml-2 mb-6 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isResumeMode ? 'Back to My Bookings' : 'Back to Lesson Details'}
          </Button>

          <header className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Complete payment
            </h1>
            <p className="mt-2 text-muted-foreground">
              Review your order and pay securely to confirm your booking.
            </p>
          </header>

          <LessonPaymentForm
            variant="page"
            student={
              selectedStudent
                ? {
                    firstName: selectedStudent.firstName,
                    lastName: selectedStudent.lastName,
                  }
                : undefined
            }
            booking={booking}
            lesson={lesson}
            customerEmail={user.email || ''}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onCancel={handlePaymentCancel}
          />
        </div>
      </div>
    );
  }

  if (isResumeMode && !showPayment && !bookingSuccess && !error) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-2">
      <div className="contents">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(paths?.lessons ?? '/academy-picker')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Lessons
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Book Lesson</h1>
          <p className="text-gray-600 mt-2">
            Confirm your lesson booking and complete payment
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Duplicate Booking Error */}
        {duplicateBookingError && (
          <Alert variant="destructive" className="mb-6">
            <XCircle className="w-4 h-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Already Booked!</strong> You already have a booking for this lesson.
                  <br />
                  <span className="text-sm">Please check your bookings or choose a different lesson.</span>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDuplicateBookingError(false)}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDuplicateBookingError(false);
                      navigate(paths?.bookings ?? '/academy-picker');
                    }}
                  >
                    View My Bookings
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Student Details Form */}
        {showStudentForm && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Student Details
                  </h2>
                  <p className="text-gray-600">
                    Enter the student information for this lesson
                  </p>
                </div>
                {students.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prefillWithLastStudent}
                    className="flex items-center"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Use Last Student
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <StudentForm
                formData={studentFormData}
                setFormData={setStudentFormData}
                onCancel={() => setShowStudentForm(false)}
                onSubmit={handleBookLesson}
                isLoading={studentFormSubmitting}
              />
            </CardContent>
          </Card>
        )}

        {/* Selected Student Display */}
        {selectedStudent && !showStudentForm && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <User className="w-8 h-8 text-blue-600 mr-3" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {selectedStudent.firstName} {selectedStudent.lastName}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Will attend this lesson
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStudentForm(true)}
                >
                  Change Student
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing Students Selection */}
        {!showStudentForm && !selectedStudent && students.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Select Student
              </h2>
              <p className="text-gray-600">
                Choose from your existing students or add a new one
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {students.map((student) => (
                  <Card
                    key={student.id}
                    className="cursor-pointer transition-all hover:shadow-md border-2 border-transparent hover:border-blue-200"
                    onClick={() => setSelectedStudent(student)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Age {calculateAge(student.dateOfBirth)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Card
                  className="cursor-pointer transition-all hover:shadow-md border-2 border-dashed border-gray-300 hover:border-blue-400"
                  onClick={() => setShowStudentForm(true)}
                >
                  <CardContent className="p-4 flex items-center justify-center">
                    <div className="text-center">
                      <Plus className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Add New Student</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lesson Details */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-2xl font-semibold text-gray-900">{lesson.name}</h2>
              <div className="flex items-center gap-2">
                {isMultiSport ? (
                  <SportBadge sport={resolveAcademySport(academySports, lesson.academySportId)!} size="md" />
                ) : null}
                <Badge variant={isFullyBooked ? "destructive" : "success"}>
                  {isFullyBooked ? 'Fully Booked' : `${spotsRemaining} spots left`}
                </Badge>
              </div>
            </div>
            <p className="text-gray-600">{lesson.description}</p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Day of Week</p>
                    <p className="font-medium">
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][lesson.daysOfWeek?.[0] ?? 0]}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-medium">{lesson.startTime} - {lesson.endTime}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Lesson Type</p>
                    <p className="font-medium capitalize">{lesson.lessonType}</p>
                  </div>
                </div>

                {lesson.skillLevel && (
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Skill Level</p>
                      <p className="font-medium capitalize">{lesson.skillLevel}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-2">Booking Summary</h3>
                  <div className="space-y-2 text-sm text-blue-800">
                    <div className="flex justify-between">
                      <span>Lesson:</span>
                      <span>{lesson.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Duration:</span>
                      <span>{lesson.startTime} - {lesson.endTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Type:</span>
                      <span className="capitalize">{lesson.lessonType}</span>
                    </div>
                    <div className="flex justify-between font-medium text-lg">
                      <span>Total:</span>
                      <span>${lesson.price}</span>
                    </div>
                  </div>
                </div>

                {booking && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="font-medium text-yellow-900 mb-2">Booking Status</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant={booking.paymentStatus === 'paid' ? 'success' : 'warning'}>
                        {booking.paymentStatus === 'paid' ? 'Confirmed' : 'Pending Payment'}
                      </Badge>
                      <span className="text-sm text-yellow-800">
                        {booking.paymentStatus === 'paid'
                          ? 'Your lesson is confirmed and ready!'
                          : 'Complete payment to confirm your booking'
                        }
                      </span>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">What's Included</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Professional tennis instruction</li>
                    <li>• Court reservation</li>
                    <li>• Equipment provided (if needed)</li>
                    <li>• Progress tracking</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking Actions */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ready to Book?</h3>
                <p className="text-gray-600">Complete your booking and payment to secure your spot</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900">${lesson.price}</p>
                </div>
                <Button
                  onClick={handleBookLesson}
                  disabled={bookingLoading || isFullyBooked || !isBookable}
                  size="lg"
                  className="flex items-center"
                  title={!isBookable ? bookingReason || 'Cannot book this lesson' : undefined}
                  isLoading={bookingLoading}
                >
                  {bookingLoading ? (
                    'Processing...'
                  ) : booking?.paymentStatus === 'pending' ? (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Complete Payment
                    </>
                  ) : !selectedStudent ? (
                    <>
                      <Users className="w-4 h-4 mr-2" />
                      {students.length > 0 ? 'Select Student & Book' : 'Add Student & Book'}
                    </>
                  ) : showStudentForm ? (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Student & Book
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Book & Pay Now
                    </>
                  )}
                </Button>
              </div>
            </div>

            {isFullyBooked && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>
                  This lesson is fully booked. Please check other available lessons.
                </AlertDescription>
              </Alert>
            )}
            {!isBookable && bookingReason && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>
                  {bookingReason}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Student Form Component
interface StudentFormProps {
  formData: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: 'male' | 'female' | 'other';
    skillLevel: 'beginner' | 'intermediate' | 'advanced';
    medicalNotes: string;
    emergencyContact: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: 'male' | 'female' | 'other';
    skillLevel: 'beginner' | 'intermediate' | 'advanced';
    medicalNotes: string;
    emergencyContact: {
      name: string;
      phone: string;
      relationship: string;
    };
  }>>;
  onCancel: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

function StudentForm({ formData, setFormData, onCancel, onSubmit, isLoading = false }: StudentFormProps) {
  return (
    <form className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name *
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name *
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Birth *
          </label>
          <input
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Gender *
          </label>
          <select
            value={formData.gender}
            onChange={(e) => setFormData({ ...formData, gender: e.target.value as 'male' | 'female' | 'other' })}
            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Skill Level
        </label>
        <select
          value={formData.skillLevel}
          onChange={(e) => setFormData({ ...formData, skillLevel: e.target.value as 'beginner' | 'intermediate' | 'advanced' })}
          className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Medical Notes
        </label>
        <textarea
          value={formData.medicalNotes}
          onChange={(e) => setFormData({ ...formData, medicalNotes: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Any medical conditions, allergies, or special requirements..."
        />
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium text-gray-900 mb-3">Emergency Contact</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.emergencyContact.name}
              onChange={(e) => setFormData({
                ...formData,
                emergencyContact: { ...formData.emergencyContact, name: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <PhoneNumberLabel htmlFor="book-lesson-ec-phone" purpose="emergency_contact" className="mb-1">
              Phone
            </PhoneNumberLabel>
            <PhoneInput
              id="book-lesson-ec-phone"
              value={formData.emergencyContact.phone}
              onChange={(phone) => setFormData({
                ...formData,
                emergencyContact: { ...formData.emergencyContact, phone },
              })}
              placeholder="Phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relationship
            </label>
            <input
              type="text"
              value={formData.emergencyContact.relationship}
              onChange={(e) => setFormData({
                ...formData,
                emergencyContact: { ...formData.emergencyContact, relationship: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Mother, Father"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="flex-1"
          isLoading={isLoading}
          onClick={onSubmit}
        >
          Continue to Booking
        </Button>
      </div>
    </form>
  );
}
